import { DropdownMenu } from '@/Components/filterbar/components/DropdownMenu';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useMemo } from 'react';
import { z } from 'zod';
import { useFilterBarCtx } from '../FilterBar';
import { FilterBadge } from '../components/FilterBadge';
import { __useFilterState } from '../useFilterState';

const baseFilterSchema = z.object({
  search: z.string().optional(),
  excludedMediaItemsInCatalog: z.boolean().optional().nullable(),
});

const catalogItemsFilterSchema = z.object({
  excludedMediaItemsInCatalog: z.boolean().optional().nullable(),
});

const catalogItemsFilterComponentProps = z.object({
  displayName: z.string().optional(),
  name: z.string().optional(),
});

type CatalogItemsFilterProps = z.infer<typeof catalogItemsFilterComponentProps>;

const MENU_OPTIONS = [
  ['Appears in Catalog', <FontAwesomeIcon key={0} icon={faCircleCheck as IconProp} />, false],
  ['Not In Catalog', <FontAwesomeIcon key={1} icon={faCircleXmark as IconProp} />, true],
] as Array<[string, React.JSX.Element, boolean]>;

const CatalogItemsFilter = ({ displayName = 'Catalog Usage', name }: CatalogItemsFilterProps) => {
  const { props: ctxProps } = useFilterBarCtx();

  const { fastFilters, slowFilters, updateFilter, isUpdating } = __useFilterState(catalogItemsFilterSchema);

  const handleSelectionChange = useCallback(
    (showUnusedOnly: boolean) => {
      updateFilter((prev) => {
        const currentValue = prev?.excludedMediaItemsInCatalog;

        if (currentValue === showUnusedOnly) {
          return {
            excludedMediaItemsInCatalog: undefined,
          };
        }

        return {
          excludedMediaItemsInCatalog: showUnusedOnly,
        };
      });
    },
    [updateFilter]
  );

  const menuItems = useMemo(() => {
    return MENU_OPTIONS.map(([name, icon, value]) => ({
      name,
      icon,
      value,
      onClick: () => {
        handleSelectionChange(value);
      },
    }));
  }, [handleSelectionChange]);

  return (
    <div style={{ position: 'relative' }}>
      <DropdownMenu
        className={ctxProps?.variant === 'default' ? 'z-btn-6' : 'z-btn-compact'}
        name={displayName || name || 'Catalog Usage'}
        menuList={menuItems}
        type="single-select"
        options={{
          filterBar: slowFilters,
          filterBarKey: 'excludedMediaItemsInCatalog',
          filterBarComparison(itemState, filterParam) {
            return itemState === filterParam;
          },
          isSearchable: false,
        }}
      />
      <FilterBadge count={!!fastFilters?.excludedMediaItemsInCatalog} isUpdating={isUpdating} />
    </div>
  );
};

const CatalogItemsFilterMemo = React.memo(CatalogItemsFilter);

export const FilterConfig_CatalogItems = {
  catalogItems: {
    searchSchema: catalogItemsFilterSchema,
    component: CatalogItemsFilterMemo,
    componentProps: catalogItemsFilterComponentProps,
  },
} as const;
